/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";
import {
	fetchReceivedBroadcastsThunk,
	setBroadcastListFilterPageNumberThunk,
	setBroadcastListInitialLoadDoneThunk,
	setBroadcastListTriggerNewSearchThunk,
} from "../../../../redux-magic/store";

/* Component Imports */

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { CircularProgress, SvgIcon, Typography } from "@mui/material";
import { useInView } from "react-intersection-observer";
import NoBroadcastIcon from "../../../../public/images/broadcast/no_broadcast.svg";
import PropertyCardBroadcast from "../../../cards/PropertyCardBroadcast/PropertyCardBroadcastFetcher";

/* Styled Components */

const RootDiv = styled("div")(({ theme }) => ({
	background: theme.palette.background.default,
	borderRadius: "0rem",
	height: "100%",
	width: "100%",
	padding: "0rem 0rem 0rem 0rem",
	[theme.breakpoints.up("md1190")]: {
		padding: "0rem 0rem 0rem 0rem",
	},
}));

const BroadcastListDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	height: "100%",
	gap: "1.5rem",
	overflow: "auto",
	overflowX: "hidden",
	padding: "1rem 1.25rem 11rem 1.25rem",
	position: "relative",
	overscrollBehavior: "contain",
	isolation: "isolate",
	"&::-webkit-scrollbar": {
		width: "6px",
		background: "transparent",
	},
	"&::-webkit-scrollbar-thumb": {
		background: "rgba(0, 0, 0, 0.2)",
		borderRadius: "3px",
		"&:hover": {
			background: "rgba(0, 0, 0, 0.3)",
		},
	},
	"&": {
		scrollbarWidth: "thin",
		scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
	},
	"&:not(:hover)::-webkit-scrollbar-thumb": {
		background: "transparent",
	},
	[theme.breakpoints.up("md1190")]: {
		// padding: "0rem 0rem 0rem 0rem",
	},
}));

const LoadMoreDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	height: "7.5rem",
	pointerEvents: "none",
	cursor: "none",
	background: "transparent",
}));

const ColumnContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
}));

const BroadcastList = ({
	session,
	dispatch,
	router,
	profile_context,
	broadcast_list,
	list_tab,
}: {
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	profile_context: any;
	broadcast_list: StateInterface["chat_states"]["broadcast_list"];
	list_tab: string;
}) => {
	/* Translations */

	// const localLang = {
	// 	broadcasts: router.locale === "ar-AE" ? "البث المباشر" : router.locale === "kn-IN" ? "ಪ್ರಸಾರಗಳು" : "Broadcasts",
	// };

	/* Fetch broadcasts on initial load */

	React.useEffect(() => {
		if ((!broadcast_list.initial_load_done && session) || list_tab === "broadcasts") {
			dispatch(setBroadcastListTriggerNewSearchThunk(true));
			dispatch(setBroadcastListInitialLoadDoneThunk(true));
			// dispatch(setBroadcastListLoadingThunk(true));
			dispatch(
				fetchReceivedBroadcastsThunk({
					is_business_profile: profile_context.is_business_profile,
					user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
					business_profile_id: profile_context.business_profile_id,
					page: 1,
					sessionId: session ? (session.session_id ? session.session_id : "") : "",
				}),
			);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [session, list_tab]);

	/* Load more broadcasts on scroll (Infinite scroll) */

	const { ref, inView } = useInView({ threshold: 0 });

	React.useEffect(() => {
		if (inView && broadcast_list.initial_load_done && session) {
			dispatch(setBroadcastListFilterPageNumberThunk(broadcast_list.filter_page_number + 1));
			// dispatch(setBroadcastListLoadingThunk(true));
			dispatch(
				fetchReceivedBroadcastsThunk({
					is_business_profile: profile_context.is_business_profile,
					user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
					business_profile_id: profile_context.business_profile_id,
					page: broadcast_list.filter_page_number ? broadcast_list.filter_page_number + 1 : 1,
					sessionId: session ? (session.session_id ? session.session_id : "") : "",
				}),
			);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inView]);

	//  Let's show banner if no broadcast found

	if (broadcast_list.broadcasts_count === 0) {
		return (
			<ColumnContainer
				sx={{
					justifyContent: "flex-start",
					width: "100%",
					// height: "100%",
					padding: "1rem",
					gap: "1rem",
				}}
			>
				<RowContainer
					sx={{
						justifyContent: "flex-start",
						width: "100%",
						borderRadius: "1rem",
						background: "#E8F4FE",
						gap: "0.75rem",
						padding: "0.75rem 1rem",
					}}
				>
					<InfoOutlinedIcon sx={{ color: "#0D3C61" }} />
					<Typography
						variant="subtitle2"
						sx={{
							fontSize: "0.875rem",
							fontWeight: 500,
							color: "#0D3C61",
						}}
					>
						{router.locale === "ar-AE"
							? "ستظهر طلبات الاتصال هنا"
							: router.locale === "kn-IN"
								? "ಸಂಪರ್ಕ ವಿನಂತಿಗಳು ಇಲ್ಲಿ ಕಾಣಿಸಿಕೊಳ್ಳುತ್ತವೆ"
								: "Connection requests will appear here"}
					</Typography>
				</RowContainer>
				<SvgIcon
					component={NoBroadcastIcon}
					inheritViewBox={false}
					viewBox="0 0 120 121"
					sx={{
						width: "18rem",
						height: "12rem",
						fill: "none",
						marginTop: "3rem",
					}}
				/>
				<Typography
					variant="subtitle2"
					color="text.secondary"
					sx={{
						fontSize: "0.875rem",
						fontWeight: 400,
					}}
				>
					{router.locale === "ar-AE"
						? "اقبل طلبات الاتصال لتنمية شبكتك"
						: router.locale === "kn-IN"
							? "ನಿಮ್ಮ ನೆಟ್‌ವರ್ಕ್ ವಿಸ್ತರಿಸಲು ಸಂಪರ್ಕ ವಿನಂತಿಗಳನ್ನು ಸ್ವೀಕರಿಸಿ"
							: "Accept connection requests to grow your network"}
				</Typography>
			</ColumnContainer>
		);
	}
	return (
		<React.Fragment>
			<RootDiv>
				{/* Broadcasts List */}

				<BroadcastListDiv>
					{broadcast_list.broadcast_ids.map((broadcast_id: string | ObjectId, index: number) => {
						/* Broadcast List Card */

						return (
							<PropertyCardBroadcast
								key={index}
								session={session}
								dispatch={dispatch}
								profile_context={profile_context}
								router={router}
								broadcast_id={broadcast_id}
								source="broadcast_list"
							/>
						);
					})}
				</BroadcastListDiv>

				{/* Load more div for infinite scroll */}

				{broadcast_list.broadcasts_count > broadcast_list.broadcast_ids.length && (
					<LoadMoreDiv ref={ref}>
						<CircularProgress />
					</LoadMoreDiv>
				)}
			</RootDiv>
		</React.Fragment>
	);
};

export default BroadcastList;
